/**
 * PRECIOS POR PAÍS — misma lógica que la landing de "De tu cocina a tus primeros pedidos".
 *
 * Importante: Impultienda es quien cobra. Los importes de esta tabla deben coincidir EXACTAMENTE
 * con los que se configuren en Markets (Impultienda → Markets) y en el producto de la tienda.
 * Países sin mercado propio usan el mercado internacional (USD), igual que el mercado "Default".
 */
export type CurrencyCode = "USD" | "ARS" | "MXN" | "COP" | "CLP" | "PEN";

export type Market = {
  currency: CurrencyCode;
  /** Locale solo para el formato de números (separadores). */
  locale: string;
  /** Precio del producto principal. */
  main: number;
  /** Precio normal (tachado) del producto principal. */
  old: number;
  /** Valor de cada bono (tachado): Bono 1, Bono 2, Bono 3. */
  bonuses: [number, number, number];
  /** Etiqueta del selector. */
  label: string;
  /** false = todavía sin precio confirmado en Impultienda: no se ofrece ni se aplica. */
  confirmed: boolean;
};

export const INTL: Market = {
  currency: "USD",
  locale: "en-US",
  main: 19,
  old: 56.99,
  bonuses: [12, 9, 7],
  label: "Internacional (USD)",
  confirmed: true,
};

// Precios cargados también en Impultienda → Markets (tienda Perro Obediente en Casa).
export const MARKETS: Record<string, Market> = {
  AR: { currency: "ARS", locale: "es-AR", main: 24990, old: 49990, bonuses: [15990, 11990, 8990], label: "Argentina", confirmed: true },
  MX: { currency: "MXN", locale: "es-MX", main: 349.99, old: 1049.99, bonuses: [219.99, 169.99, 129.99], label: "México", confirmed: true },
  CO: { currency: "COP", locale: "es-CO", main: 61990, old: 184990, bonuses: [39990, 29990, 22990], label: "Colombia", confirmed: true },
  CL: { currency: "CLP", locale: "es-CL", main: 18990, old: 56990, bonuses: [11990, 8990, 6990], label: "Chile", confirmed: true },
  PE: { currency: "PEN", locale: "es-PE", main: 64.99, old: 194.99, bonuses: [41.99, 30.99, 24.99], label: "Perú", confirmed: true },
};

type Preference = "auto" | "INTL" | string;

const PREF_KEY = "aura-pais-preferencia-perro";
const COUNTRY = /^[A-Z]{2}$/;
const TIMEOUT_MS = 3500;

function marketFor(country: string | null): Market {
  if (!country) return INTL;
  const m = MARKETS[country];
  return m && m.confirmed ? m : INTL;
}

function format(amount: number, m: Market): { cur: string; num: string } {
  const hasCents = !Number.isInteger(amount);
  const opts = {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
    useGrouping: "always",
  } as unknown as Intl.NumberFormatOptions;
  return { cur: m.currency === "USD" ? "US$" : m.currency, num: new Intl.NumberFormat(m.locale, opts).format(amount) };
}

/** Texto corto con moneda, p. ej. "US$12" o "ARS 15.990". */
function money(amount: number, m: Market): string {
  const f = format(amount, m);
  return m.currency === "USD" ? `${f.cur}${f.num}` : `${f.cur} ${f.num}`;
}

function readPref(): Preference {
  try {
    const v = window.localStorage.getItem(PREF_KEY);
    if (v === "INTL" || (v && MARKETS[v]?.confirmed)) return v;
  } catch {
    /* sin almacenamiento */
  }
  return "auto";
}

function storePref(p: Preference) {
  try {
    if (p === "auto") window.localStorage.removeItem(PREF_KEY);
    else window.localStorage.setItem(PREF_KEY, p);
  } catch {
    /* vale solo para esta visita */
  }
}

async function fetchCountry(url: string, field: string): Promise<string | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: controller.signal, credentials: "omit", referrerPolicy: "no-referrer", cache: "no-store" });
    if (!r.ok) return null;
    const data = (await r.json()) as Record<string, unknown>;
    const raw = typeof data?.[field] === "string" ? (data[field] as string).trim().toUpperCase() : "";
    return COUNTRY.test(raw) ? raw : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function countryFromLanguage(): string | null {
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const l of langs) {
    const region = l?.split(/[-_]/)[1]?.toUpperCase();
    if (region && COUNTRY.test(region)) return region;
  }
  return null;
}

/** Orden: country.is → ipapi.co (solo si el primero falla) → idioma del navegador → USD. */
async function detectCountry(): Promise<string | null> {
  return (
    (await fetchCountry("https://api.country.is/", "country")) ??
    (await fetchCountry("https://ipapi.co/json/", "country_code")) ??
    countryFromLanguage()
  );
}

const CARET =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%2316302a' stroke-width='1.6' stroke-linecap='round'/%3E%3C/svg%3E\")";

const STYLE = `
.geo{display:flex;align-items:center;justify-content:center;gap:8px;flex-wrap:wrap;margin-bottom:18px;font-size:.85rem;color:var(--mute)}
.geo label{font-weight:500}
.geo select{appearance:none;-webkit-appearance:none;border:1px solid rgba(22,48,42,.25);background:#fff ${CARET} no-repeat right 12px center;border-radius:99px;padding:8px 32px 8px 14px;font:600 .85rem Inter,sans-serif;color:var(--ink);cursor:pointer}
.geo select:focus-visible{outline:3px solid var(--honey);outline-offset:2px}
.price-old{font-size:.95rem;color:var(--mute);margin:0 0 4px}
.price-old s,.bval s,.bsave s,.incl .bv{text-decoration-thickness:2px;text-decoration-color:#c0392b}
.bval{margin:8px 0 0;font-size:.92rem;color:var(--mute)}
.bval b{color:var(--clay)}
.bsave{margin:12px 0 0;font-size:1rem;color:var(--ink)}
.bsave b{color:var(--clay)}
.incl .bv{margin-left:auto;flex:none;align-self:center;font-size:.74rem;color:var(--mute);white-space:nowrap;text-decoration-line:line-through}
.incl .bv + .g{margin-left:8px}
`;

/** Dibuja el selector y mantiene precios, tachados y valor de bonos según el país. */
export function initPricing(root: HTMLElement): () => void {
  const geo = root.querySelector<HTMLElement>("#geo");
  const price = root.querySelector<HTMLElement>("#price");
  const heroPrice = root.querySelector<HTMLElement>("#heroPrice");
  const note = root.querySelector<HTMLElement>("#pnote2");
  if (!geo || !price || !note) return () => undefined;

  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  // --- Elementos de tachado (se agregan una sola vez) ---
  const added: HTMLElement[] = [];
  const make = (tag: string, cls: string, html = ""): HTMLElement => {
    const el = document.createElement(tag);
    el.className = cls;
    el.innerHTML = html;
    added.push(el);
    return el;
  };

  const oldPrice = make("div", "price-old", "Precio normal <s></s>");
  price.parentElement?.insertBefore(oldPrice, price);
  const oldPriceVal = oldPrice.querySelector<HTMLElement>("s")!;

  const bonusCards = Array.from(root.querySelectorAll<HTMLElement>(".bono .bd"));
  const bonusNodes: HTMLElement[] = [];
  bonusCards.slice(0, 3).forEach((bd, i) => {
    const el = make("p", "bval", "Valor: <s></s> · <b>Hoy, incluido sin costo</b>");
    const h3 = bd.querySelector("h3");
    if (h3) h3.insertAdjacentElement("afterend", el);
    else bd.prepend(el);
    bonusNodes[i] = el.querySelector<HTMLElement>("s")!;
  });

  const listNodes: HTMLElement[] = [];
  Array.from(root.querySelectorAll<HTMLElement>(".incl li .g"))
    .slice(0, 3)
    .forEach((g, i) => {
      const el = document.createElement("s");
      el.className = "bv";
      added.push(el);
      g.insertAdjacentElement("beforebegin", el);
      listNodes[i] = el;
    });

  const bsum = root.querySelector<HTMLElement>(".bsum");
  let totalNode: HTMLElement | null = null;
  if (bsum) {
    const el = make("p", "bsave", "Valor total de los 3 bonos: <s></s> · <b>Hoy, incluidos</b>");
    const firstP = bsum.querySelector("p");
    if (firstP) firstP.insertAdjacentElement("afterend", el);
    else bsum.appendChild(el);
    totalNode = el.querySelector<HTMLElement>("s");
  }

  const options: Array<[string, string]> = [
    ["auto", "Automático"],
    ...Object.entries(MARKETS)
      .filter(([, m]) => m.confirmed)
      .map(([code, m]) => [code, m.label] as [string, string]),
    ["INTL", INTL.label],
  ];

  const id = "geo-select";
  geo.innerHTML = `<label for="${id}">Ver precios para</label><select id="${id}">${options
    .map(([v, l]) => `<option value="${v}">${l}</option>`)
    .join("")}</select>`;
  const select = geo.querySelector<HTMLSelectElement>("select")!;

  // ?pais=XX solo para pruebas (no cambia lo que cobra Impultienda).
  const urlCountry = (() => {
    const c = new URLSearchParams(window.location.search).get("pais")?.trim().toUpperCase() ?? "";
    return COUNTRY.test(c) ? c : null;
  })();

  let pref: Preference = readPref();
  let detected: string | null = null;
  let pending = true;

  const render = () => {
    let market: Market;
    if (urlCountry) market = marketFor(urlCountry);
    else if (pref === "INTL") market = INTL;
    else if (pref !== "auto") market = marketFor(pref);
    else market = pending ? INTL : marketFor(detected);

    const f = format(market.main, market);
    const html = `<small>${f.cur}</small>${f.num}`;
    price.innerHTML = html;
    if (heroPrice) heroPrice.innerHTML = html;
    note.textContent =
      market.currency === "USD" ? "Pago único en dólares · Sin suscripciones" : "Pago único · Sin suscripciones";

    oldPriceVal.textContent = money(market.old, market);
    market.bonuses.forEach((v, i) => {
      const text = money(v, market);
      if (bonusNodes[i]) bonusNodes[i].textContent = text;
      if (listNodes[i]) listNodes[i].textContent = text;
    });
    if (totalNode) totalNode.textContent = money(Math.round(market.bonuses.reduce((a, b) => a + b, 0) * 100) / 100, market);

    select.value = urlCountry && MARKETS[urlCountry]?.confirmed ? urlCountry : pref;
  };

  const onChange = () => {
    pref = select.value;
    storePref(pref);
    render();
  };
  select.addEventListener("change", onChange);

  const state = { cancelled: false };
  detectCountry().then((c) => {
    if (state.cancelled) return;
    detected = c;
    pending = false;
    render();
  });

  render();

  return () => {
    state.cancelled = true;
    select.removeEventListener("change", onChange);
    style.remove();
    added.forEach((el) => el.remove());
  };
}
